import { BsTrash } from '@react-icons/all-files/bs/BsTrash';
import { format } from 'date-fns';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, humanizeBytes, inject, Modal, ModalHandle, subUnitsOfTime, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { Button } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { FileSystemAttachmentsModal } from './FileSystemAttachmentsModal';
import { FileSystemsStore } from './FileSystemsStore';

export interface IFileSystemItemProps {
    readonly fileSystem: Mri.FileSystems.FileSystem;
}

@autoinject
@observer
export class FileSystemItem extends React.Component<IFileSystemItemProps> {
    public render() {
        const fileSystem = this.props.fileSystem;
        const version = fileSystem.softwareVersion;
        return (
            <tr>
                <td>
                    {[
                        ...[
                            version.productType,
                            version.majorRevision,
                            version.minorRevision,
                            version.extRevision,
                            version.auxRevision,
                        ],
                    ].join('.')}
                </td>

                <td>{fileSystem.updated ? format(fileSystem.updated, 'MM/d/yyyy') : '-'}</td>

                <td>
                    {!fileSystem.fileSize && (
                        <Tooltip message="File System not available for Download">{fileSystem.fileName}</Tooltip>
                    )}
                    {fileSystem.fileSize && (
                        <a href={fileSystem.url} className="btn btn-link p-0" style={{ fontWeight: 'normal' }}>
                            {fileSystem.fileName}
                            {this.isNewFilesystem && <span className="mx-2 badge bg-primary">New</span>}
                        </a>
                    )}

                    {this.props.fileSystem.softwareVersion.fsType == 17 && (
                        <Tooltip message="Delete" delay={0}>
                            <Button color="danger" onClick={this.deleteFileSystem}>
                                <BsTrash />
                            </Button>
                        </Tooltip>
                    )}
                </td>

                <td style={{ minWidth: '90px' }}>{humanizeBytes(fileSystem.fileSize)}</td>

                <td>{this.renderDependencies()}</td>

                <td className="article-body">
                    <div dangerouslySetInnerHTML={{ __html: fileSystem.changeLog || '-' }} />
                </td>

                <Modal
                    className="modal-md"
                    title="Confirm Delete"
                    modalHandle={this.confirmModalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <p className="text-center">Are you sure you want to Delete {this.props.fileSystem.fileName}?</p>
                    {this.manifestNames.length > 0 && (
                        <div className="text-center">
                            <p>This will also delete manifest(s):</p>
                            {this.manifestNames.map((name, i) => (
                                <p key={i}>{name}</p>
                            ))}
                        </div>
                    )}
                </Modal>
            </tr>
        );
    }

    private renderDependencies() {
        const fileSystem = this.props.fileSystem;

        const fileSystemsDeps = fileSystem.dependencies
            ? fileSystem.dependencies.map(d => this.fileSystemsStore.fileSystems.get(d)!).filter(d => !!d)
            : [];

        return (
            <>
                {fileSystemsDeps.map(d => (
                    <div key={d.fileSystemGuid}>
                        <a href={d.url} className="btn btn-link p-0" style={{ fontWeight: 'normal' }}>
                            {d.fileName}
                        </a>
                    </div>
                ))}
                <div>
                    {!!fileSystemsDeps.length && (
                        <>
                            <button
                                className="btn btn-link btn-xs p-0"
                                onClick={this.downloadAll}
                                style={{ fontWeight: 'normal' }}
                            >
                                <small>Download All</small>
                            </button>
                            &nbsp;<b>&middot;</b>&nbsp;
                        </>
                    )}
                    <button
                        className="btn btn-link btn-xs p-0"
                        onClick={this.openAttachmentsModal}
                        style={{ fontWeight: 'normal' }}
                    >
                        <small>{fileSystem.attachments?.length ? 'View Attachements' : 'Add Attachment'}</small>
                    </button>
                </div>
                <FileSystemAttachmentsModal fileSystem={fileSystem} modalHandle={this.attachmentsModal} />
            </>
        );
    }

    @bind
    private async downloadAll() {
        await this.fileSystemsStore.downloadAllAsync(this.props.fileSystem.fileSystemGuid);
    }

    @bind
    private openAttachmentsModal() {
        this.attachmentsModal.open();
    }

    @bind
    @action
    private async deleteFileSystem(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        this.manifestNames =
            (await this.fileSystemsStore.getManifestNamesForFileSystem(this.props.fileSystem.fileSystemGuid)) ?? [];
        this.confirmModalHandle.open();

        if (await this.confirmModalHandle.confirm()) {
            try {
                this.fileSystemsStore.deleteFileSystemFromStorageAsync(this.props.fileSystem.fileSystemGuid);
            } catch (e: any) {
                console.error(e);
                this.appStore.alertHandle.setError(e);
            }
        }
    }

    @computed
    private get isNewFilesystem() {
        if (!this.props.fileSystem.updated) return false;

        return this.props.fileSystem.updated > subUnitsOfTime(new Date(), 48, 'hours');
    }

    private attachmentsModal = new ModalHandle();
    private confirmModalHandle = new ModalHandle();
    @observable
    private manifestNames: string[] = [];
    @inject
    private fileSystemsStore!: FileSystemsStore;
    @inject
    private appStore!: AppStore;
}
